import { useCallback } from "react";
import {
  APPLY_RETROACTIVE_TAG,
  askrIngestrClients,
  DELETE_OPERATION,
} from "providers/AskrProvider";
import { useMutation } from "@apollo/client";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import {
  useRetrotagTagPath,
  useRetrotagSegmentRange,
} from "utils/customHooks/retrotagSliceSelectors";
import {
  useProcessQuery,
  useRefetchMutatedSegments,
} from "./retrotagQueryHooks";
import { TimeRangeFinite } from "types/types";

/** Returns an askr_ingestr mutation for a machemetryId */
export function useApplyRetrotagMutation({
  machemetryId,
  skip,
}: {
  machemetryId: string;
  skip: boolean;
}) {
  const range = useRetrotagSegmentRange();
  const refetchMutatedSegments = useRefetchMutatedSegments({
    machemetryId,
    retrotaggedSegmentRange: range,
  });

  // gets the midgard id from askr to be used in the askr applyTag mutation
  const [processFromAskr] = useProcessQuery({
    machemetryId,
    skip,
  });
  const midgardProcessId = processFromAskr?.id;
  const tagPath = useRetrotagTagPath();

  const ckey = useCustomerKey();
  const askrIngestrClient = askrIngestrClients.get(ckey);
  const [mutateSegment, mutateSegmentResult] = useMutation(
    APPLY_RETROACTIVE_TAG,
    { client: askrIngestrClient }
  );

  const handleMutateSegment = useCallback(async () => {
    const variables = {
      id: midgardProcessId,
      ge: String(range?.ge),
      lt: String(range?.lt),
      tagPath,
    };
    const result = await mutateSegment({ variables });
    await refetchMutatedSegments();
    return result;
  }, [
    midgardProcessId,
    mutateSegment,
    range?.ge,
    range?.lt,
    refetchMutatedSegments,
    tagPath,
  ]);
  // tslint:disable-next-line
  return [handleMutateSegment, mutateSegmentResult] as const;
}

/**
 * Returns an askr_ingestr mutation for deleting a retrotag specified by
 * a segment's retrotag event operationId
 *
 * @operationId: The askr-assigned operationId for the segment's retrotag event to be removed
 * @machemetryId: Machemetry process id for the segment's retrotag to be removed
 * @segmentRange: Range of the segment to be refetched after removing the retrotag
 */
export function useDeleteRetrotag({
  operationId,
  machemetryId,
  segmentRange,
}: {
  operationId: string;
  machemetryId: string;
  segmentRange: TimeRangeFinite;
}) {
  const refetchMutatedSegments = useRefetchMutatedSegments({
    machemetryId,
    retrotaggedSegmentRange: segmentRange,
  });

  const variables = { operationId };
  const ckey = useCustomerKey();
  const askrIngestrClient = askrIngestrClients.get(ckey);
  const [mutateSegment, mutateSegmentResult] = useMutation(DELETE_OPERATION, {
    client: askrIngestrClient,
  });

  const handleMutateSegment = async () => {
    const result = await mutateSegment({ variables });
    await refetchMutatedSegments();
    return result;
  };
  // tslint:disable-next-line
  return [handleMutateSegment, mutateSegmentResult] as const;
}
